<template>
    <!-- Hidden audio players (audio-only; video rendered separately) -->
    <div class="gcw-audio-players">
        <div v-for="player in players" :key="player.uid" class="gcw-audio-player-slot" :aria-volume="player.lastSound">
            <CallPlayer @stream-request="onStreamRequest" v-if="inCall" :streamid="player.streamid"
                :stick="player.stick" :uid="player.uid" :self="player.self" :gvolume="volume" :gmuted="muted"
                :name="player.name" :image="player.image" :modmuted="player.muted" :audio="player.audio"
                :video="player.video && !player.audioMode && !audioMode" :adminopts="false" :volume="player.volume"
                room="room-page" />
        </div>
    </div>

    <audio ref="roomSound" :src="soundSrc"></audio>
    <audio ref="roomSoundLeave" :src="soundSrcLeave"></audio>

    <!-- ============  MINIMIZED PILL  ============ -->
    <div v-if="joinedCall && isMinimized" class="gcw-minimized d-flex align-items-center position-fixed rounded-pill user-select-none"
        :class="{ 'gcw-dragging': isDragging }"
        :style="minimizedStyle" ref="minimizedEl" role="region" :aria-label="$t('Active call')"
        @mousedown.prevent="startDrag" @touchstart.prevent="startDrag">
        <span class="gcw-mini-label">{{ callTitle }}</span>
        <span class="gcw-mini-count"><i class="fas fa-user-group" aria-hidden="true"></i> {{ participantsCount }}</span>
        <button class="gcw-mini-btn" type="button" :title="$t('Expand')" :aria-label="$t('Expand')" @click.stop="expandWidget">
            <i class="fas fa-expand" aria-hidden="true"></i>
        </button>
        <button class="gcw-mini-btn gcw-mini-btn-leave" type="button" :title="$t('Leave Call')" :aria-label="$t('Leave Call')" @click.stop="() => leaveCall()">
            <i class="fas fa-phone-slash" aria-hidden="true"></i>
        </button>
    </div>

    <!-- ============  EXPANDED WIDGET  ============ -->
    <div v-if="joinedCall && !isMinimized" class="gcw-widget"
        :class="{ 'gcw-expanded': panelExpanded, 'gcw-dragging': isDragging, 'gcw-resizing': isResizing, 'gcw-column': isColumnMode, 'gcw-height-pinned': widgetHeight > 0 }"
        :style="widgetStyle" ref="widgetEl" role="region" :aria-label="$t('Active call')">

        <!-- Main body (topbar + video + bar) -->
        <div class="gcw-widget-body">

        <!-- TOP BAR: drag, resize, window controls -->
        <div class="gcw-topbar d-flex align-items-center justify-content-between flex-shrink-0 user-select-none" @mousedown.prevent="startDrag" @touchstart.prevent="startDrag">
            <div class="gcw-topbar-left d-flex align-items-center flex-grow-1 overflow-hidden">
                <span class="gcw-live-dot" aria-hidden="true"></span>
                <span class="gcw-topbar-title text-truncate">{{ callTitle }}</span>
                <span class="gcw-topbar-count">
                    <i class="fas fa-user-group" aria-hidden="true"></i> {{ participantsCount }}
                </span>
                <!-- Status indicators -->
                <span v-if="hand" class="gcw-topbar-indicator gcw-topbar-indicator-hand rounded-pill" :title="$t('Hand raised')">
                    <i class="fas fa-hand" aria-hidden="true"></i>
                </span>
                <span v-if="recording" class="gcw-topbar-indicator gcw-topbar-indicator-rec rounded-pill" :title="$t('Recording in progress')">
                    <span class="gcw-topbar-rec-dot" aria-hidden="true"></span>
                    <span>REC</span>
                </span>
            </div>
            <div class="gcw-topbar-right d-flex align-items-center flex-shrink-0">
                <button class="gcw-topbar-btn" type="button" :title="$t('Go to call')" :aria-label="$t('Go to call')" @click.stop="navigateToCall">
                    <i class="fas fa-arrow-up-right-from-square" aria-hidden="true"></i>
                </button>
                <button class="gcw-topbar-btn" type="button" :title="$t('Minimize')" :aria-label="$t('Minimize')" @click.stop="minimizeWidget">
                    <i class="fas fa-window-minimize" aria-hidden="true"></i>
                </button>
                <button class="gcw-topbar-btn gcw-topbar-btn-close" type="button" :title="$t('Send to corner')" :aria-label="$t('Send to corner')" @click.stop="closeToBottomRight">
                    <i class="fas fa-xmark" aria-hidden="true"></i>
                </button>
            </div>
        </div>

        <!-- Video area — active speaker + self PiP -->
        <div class="gcw-video-area" ref="videoArea"
            :class="{ 'gcw-video-speaking': activeSpeakerPlayer && activeSpeakerPlayer.audio && !activeSpeakerPlayer.muted }"
            @click="pinnedUid ? pinPlayer(pinnedUid) : undefined">
            <!-- Main speaker video (crossfade pair) -->
            <video ref="mainVideo" class="gcw-main-video" autoplay playsinline muted
                :class="{ 'gcw-hidden': !speakerHasVideo }"
            ></video>
            <video ref="mainVideoPrev" class="gcw-main-video gcw-main-video-prev" autoplay playsinline muted
                :class="{ 'gcw-crossfade-out': crossfadeState === 'fading' }"
            ></video>

            <!-- No-video placeholder for active speaker -->
            <div v-if="!speakerHasVideo" class="gcw-no-video-placeholder position-absolute d-flex flex-column align-items-center justify-content-center">
                <template v-if="activeSpeakerPlayer">
                    <div class="gcw-avatar-ring-wrap position-relative d-inline-flex align-items-center justify-content-center">
                        <template v-if="activeSpeakerPlayer.image">
                            <img class="gcw-speaker-avatar" :src="activeSpeakerPlayer.image" :alt="activeSpeakerPlayer.name" />
                        </template>
                        <template v-else>
                            <div class="gcw-speaker-initials text-uppercase rounded-circle">{{ initials(activeSpeakerPlayer.name) }}</div>
                        </template>
                        <div v-if="activeSpeakerPlayer.audio && !activeSpeakerPlayer.muted" class="gcw-speaker-ring" aria-hidden="true"></div>
                    </div>
                    <div v-if="activeSpeakerPlayer.audio && !activeSpeakerPlayer.muted" class="gcw-audio-wave d-flex align-items-end" aria-hidden="true">
                        <span></span><span></span><span></span><span></span><span></span>
                    </div>
                    <div class="gcw-placeholder-name d-flex align-items-center">
                        <i v-if="activeSpeakerPlayer.audio && !activeSpeakerPlayer.muted" class="fas fa-microphone gcw-speaker-mic-icon" aria-hidden="true"></i>
                        <span>{{ activeSpeakerPlayer.name }}</span>
                    </div>
                </template>
                <template v-else>
                    <div class="gcw-speaker-initials text-uppercase rounded-circle">
                        <i class="fas fa-phone"></i>
                    </div>
                </template>
            </div>

            <!-- Speaker name overlay -->
            <div v-if="activeSpeakerPlayer" class="gcw-speaker-name">
                <span>{{ activeSpeakerPlayer.name }}</span>
            </div>

            <!-- Pinned badge on main video area -->
            <div v-if="pinnedUid" class="gcw-pinned-badge" :title="$t('Pinned — click to unpin')" @click.stop="pinPlayer(pinnedUid)">
                <i class="fas fa-thumbtack" aria-hidden="true"></i>
                <span>{{ $t("Pinned") }}</span>
            </div>

            <!-- Raised-hand badge on active speaker -->
            <div v-if="activeSpeakerPlayer && activeSpeakerPlayer.hand" class="gcw-hand-badge" :title="$t('Hand raised')">
                <i class="fas fa-hand" aria-hidden="true"></i>
            </div>

            <!-- Subtitles overlay -->
            <div v-if="subtitlesActive && groupedSubtitles.length" class="gcw-subtitles-overlay" ref="subtitlesContainer">
                <div v-for="(group, gIdx) in groupedSubtitles" :key="gIdx"
                     class="gcw-subtitle-line"
                     :data-speaker-color="speakerColorIndex(group.user)"
                     :data-last="gIdx === groupedSubtitles.length - 1 || undefined">
                    <div class="gcw-subtitle-header">
                        <div class="gcw-subtitle-avatar" aria-hidden="true">{{ group.user.charAt(0).toUpperCase() }}</div>
                        <span class="gcw-subtitle-name">{{ group.user }}</span>
                    </div>
                    <div class="gcw-subtitle-bubble">
                        <span v-for="(line, lIdx) in group.lines" :key="lIdx" class="gcw-subtitle-text">{{ line }}</span>
                    </div>
                </div>
            </div>

            <!-- PiP column: remote participant thumbnails stacked above self -->
            <div class="gcw-pip-column" v-if="selfPlayer || pipStripPlayers.length > 0">
                <!-- Overflow button: shown when more participants than PIP_STRIP_MAX -->
                <button v-if="pipStripOverflow > 0"
                    class="gcw-pip-strip-overflow"
                    type="button"
                    :title="$t('Go to call')"
                    :aria-label="$t('Go to call')"
                    @click.stop="navigateToCall">
                    <span>+{{ pipStripOverflow }}</span>
                </button>

                <!-- Remote participant thumbnails (up to PIP_STRIP_MAX) -->
                <div v-for="p in pipStripPlayers" :key="p.uid"
                    class="gcw-pip-strip-item"
                    :class="{ 'gcw-pip-strip-item--speaking': speakingUid === p.uid && !p.muted && p.audio, 'gcw-pip-strip-item--pinned': pinnedUid === p.uid }"

                    :title="pinnedUid === p.uid ? $t('Click to unpin') : $t('Click to pin')"
                    @click.stop="pinPlayer(p.uid)">
                    <video
                        :ref="(el) => _assignStripVideo(el as HTMLVideoElement | null, p)"
                        class="gcw-pip-strip-video"
                        autoplay playsinline muted
                        :class="{ 'gcw-hidden': !(p.video && !p.audioMode && !audioMode && p.streamid) }"
                    ></video>
                    <div v-if="!(p.video && !p.audioMode && !audioMode && p.streamid)" class="gcw-self-avatar-wrap">
                        <template v-if="p.image">
                            <img class="gcw-self-avatar-img rounded-circle" :src="p.image" :alt="p.name" />
                        </template>
                        <template v-else>
                            <div class="gcw-self-avatar-initials text-uppercase rounded-circle">{{ initials(p.name) }}</div>
                        </template>
                    </div>
                    <div class="gcw-self-pip-label">{{ p.name }}</div>
                    <!-- Pin badge -->
                    <div v-if="pinnedUid === p.uid" class="gcw-pip-pin-badge" aria-hidden="true">
                        <i class="fas fa-thumbtack"></i>
                    </div>
                </div>

                <!-- Self PiP (always at the bottom of the column) -->
                <div class="gcw-self-pip" v-if="selfPlayer">
                    <video ref="selfVideo" class="gcw-self-video" autoplay playsinline muted
                        :class="{ 'gcw-hidden': !selfHasVideo }"
                    ></video>
                    <div v-if="!selfHasVideo" class="gcw-self-avatar-wrap">
                        <template v-if="selfPlayer.image">
                            <img class="gcw-self-avatar-img rounded-circle" :src="selfPlayer.image" :alt="selfPlayer.name" />
                        </template>
                        <template v-else>
                            <div class="gcw-self-avatar-initials text-uppercase rounded-circle">{{ initials(selfPlayer.name) }}</div>
                        </template>
                    </div>
                    <div class="gcw-self-pip-label">{{ $t("You") }}</div>
                </div>
            </div>
        </div>

        <!-- Control bar — panels | media | leave -->
        <div class="gcw-bar d-flex align-items-center justify-content-between flex-shrink-0 w-100">
            <!-- LEFT: Panel toggles -->
            <div class="gcw-bar-left d-flex align-items-center flex-shrink-0">
                <button class="gcw-bar-btn gcw-bar-btn-participants"
                    :class="{ 'gcw-bar-btn-active': panelExpanded && activePanel === 'participants' }"
                    :title="$t('Participants')" :aria-label="$t('Participants')" type="button" @click="openPanel('participants')">
                    <i class="fas fa-users" aria-hidden="true"></i>
                    <span class="gcw-participant-count">{{ participantsCount > 9 ? '9+' : participantsCount }}</span>
                </button>
                <button class="gcw-bar-btn gcw-bar-btn-chat"
                    :class="{ 'gcw-bar-btn-active': panelExpanded && activePanel === 'chat' }" :title="$t('Chat')"
                    :aria-label="$t('Chat')" type="button"
                    @click="openPanel('chat')">
                    <i class="fas fa-comment" aria-hidden="true"></i>
                <span v-if="unreadChat > 0" class="gcw-unread-badge rounded-pill">{{ unreadChat > 9 ? '9+' : unreadChat }}</span>
                </button>
            </div>

            <!-- CENTER: Media controls -->
            <div class="gcw-bar-center d-flex align-items-center flex-shrink-0">
                <button class="gcw-bar-btn gcw-bar-btn-lg" :class="{ 'gcw-bar-btn-off': !audio, 'gcw-bar-btn-warn': !micAvailable }"
                    :title="!micAvailable ? $t('Microphone not detected') : audio ? $t('Disable audio') : $t('Enable audio')"
                    :aria-label="!micAvailable ? $t('Microphone not detected') : audio ? $t('Disable audio') : $t('Enable audio')" type="button" @click="toggleAudio">
                    <i :class="audio ? 'fas fa-microphone' : 'fas fa-microphone-slash'" aria-hidden="true"></i>
                </button>

                <button v-if="!audioMode" class="gcw-bar-btn gcw-bar-btn-lg" :class="{ 'gcw-bar-btn-off': !video }"
                    :title="video ? $t('Disable video') : $t('Enable video')" :aria-label="video ? $t('Disable video') : $t('Enable video')" type="button" @click="toggleVideo">
                    <i :class="video ? 'fas fa-video' : 'fas fa-video-slash'" aria-hidden="true"></i>
                </button>

                <!-- Options gear + dropdown anchored here -->
                <div class="gcw-options-anchor">
                    <button class="gcw-bar-btn gcw-bar-btn-lg" :class="{ 'gcw-bar-btn-active': optionsMenuOpen }"
                        type="button" :title="$t('More options')" :aria-label="$t('More options')" aria-haspopup="menu" :aria-expanded="optionsMenuOpen"
                        @click.stop="toggleOptionsMenu">
                        <i class="fas fa-gear" aria-hidden="true"></i>
                        <span v-if="recording" class="gcw-bar-rec-dot" aria-hidden="true"></span>
                    </button>

                    <!-- ============  OPTIONS DROPDOWN MENU  ============ -->
                    <Transition name="gcw-menu-fade">
                        <div v-if="optionsMenuOpen" class="gcw-options-menu" @click.stop>

                            <!-- Recording indicator -->
                            <div v-if="recording" class="gcw-menu-banner gcw-menu-banner-rec d-flex align-items-center fw-semibold">
                                <i class="fas fa-circle gcw-rec-pulse" aria-hidden="true"></i>
                                <span>{{ $t("Recording in progress") }}</span>
                            </div>

                            <!-- Share call -->
                            <button v-if="callShareMode !== 'private'" class="gcw-menu-item" type="button" @click="openShareMenu(); closeOptionsMenu();">
                                <i class="fa-solid fa-share" aria-hidden="true"></i>
                                <span class="gcw-menu-item-label">{{ $t("Share Call") }}</span>
                            </button>

                            <!-- Raise / Lower hand -->
                            <button class="gcw-menu-item" type="button" :class="{ 'gcw-menu-item-active': hand }" @click="toggleHand(); closeOptionsMenu();">
                                <i class="fas fa-hand" aria-hidden="true"></i>
                                <span class="gcw-menu-item-label">{{ hand ? $t("Lower hand") : $t("Raise hand") }}</span>
                            </button>

                            <div class="gcw-menu-divider"></div>

                            <!-- Subtitles: click activates when OFF -->
                            <button v-if="!subtitlesActive" class="gcw-menu-item" type="button" @click.stop="handleSubtitlesClick">
                                <i class="far fa-closed-captioning" aria-hidden="true"></i>
                                <span class="gcw-menu-item-label">{{ $t("Subtitles") }}</span>
                            </button>

                            <!-- Disable subtitles — only shown when active -->
                            <button v-if="subtitlesActive" class="gcw-menu-item gcw-menu-item-danger" type="button" @click="toggleSubtitles">
                                <i class="fas fa-closed-captioning" aria-hidden="true"></i>
                                <span class="gcw-menu-item-label">{{ $t("Disable subtitles") }}</span>
                            </button>

                            <!-- Language row + flyout (relative anchor) — only shown when subtitles active -->
                            <div v-if="subtitlesActive" class="gcw-menu-item-relative">
                                <button class="gcw-menu-item" :class="{ 'gcw-menu-item-active': languageSubmenuOpen }" type="button" @click.stop="languageSubmenuOpen = !languageSubmenuOpen">
                                    <i class="fas fa-language" aria-hidden="true"></i>
                                    <span class="gcw-menu-item-label">{{ $t("Language") }}</span>
                                    <span class="gcw-menu-item-badge">{{ languages.find(l => l.code === languageSelected)?.name }}</span>
                                    <i class="fas fa-chevron-right gcw-menu-item-chevron" :class="{ 'gcw-rotated': languageSubmenuOpen }" aria-hidden="true"></i>
                                </button>
                                <!-- Language flyout — anchored to this row, floats to the left -->
                                <Transition name="gcw-menu-fade">
                                    <div v-if="languageSubmenuOpen" class="gcw-language-menu" @click.stop>
                                        <div class="gcw-submenu-header">{{ $t("Language") }}</div>
                                        <button v-for="lang in languages" :key="lang.code"
                                            class="gcw-menu-item"
                                            type="button"
                                            @click="selectSubtitleLanguage(lang.code)">
                                            <i :class="languageSelected === lang.code ? 'fas fa-check gcw-icon-on' : 'far fa-circle'" aria-hidden="true"></i>
                                            <span>{{ lang.name }}</span>
                                        </button>
                                    </div>
                                </Transition>
                            </div>

                            <!-- Start / Stop Recording -->
                            <button v-if="!recording && (canAdmin || canModerate)" class="gcw-menu-item" type="button" @click="startRecord">
                                <i class="far fa-circle-dot" aria-hidden="true"></i>
                                <span class="gcw-menu-item-label">{{ $t("Start recording") }}</span>
                            </button>
                            <button v-if="recording && recordingAdmin === selfId && (canAdmin || canModerate)" class="gcw-menu-item gcw-menu-item-danger" type="button" @click="stopRecordFromMenu">
                                <i class="far fa-circle-stop" aria-hidden="true"></i>
                                <span class="gcw-menu-item-label">{{ $t("Stop recording") }}</span>
                            </button>

                            <!-- Volume slider -->
                            <div class="gcw-menu-divider"></div>
                            <div class="gcw-menu-volume-row d-flex align-items-center" @click.stop>
                                <button class="gcw-menu-volume-btn" type="button" @click="toggleMuted" :title="muted ? $t('Unmute') : $t('Mute')" :aria-label="muted ? $t('Unmute') : $t('Mute')">
                                    <i :class="muted || volume <= 0 ? 'fas fa-volume-xmark' : volume <= 0.5 ? 'fas fa-volume-low' : 'fas fa-volume-high'" aria-hidden="true"></i>
                                </button>
                                <input type="range" class="gcw-volume-slider" min="0" max="100" :value="Math.round(volume * 100)"
                                    :style="{ '--gcw-vol': Math.round(volume * 100) + '%' }"
                                    :aria-label="$t('Volume')" :aria-valuenow="Math.round(volume * 100)"
                                    @input="onVolumeInput($event)" />
                            </div>

                            <div class="gcw-menu-divider"></div>
                            <div class="gcw-device-row" @click.stop>
                                <label class="gcw-device-label">{{ $t("Microphone") }}</label>
                                <select class="gcw-device-select" :value="selectedAudioInputIndex" @change="onAudioInputDeviceChange">
                                    <option v-for="(d, i) in audioInputDevices" :key="`ain-${i}`" :value="i">{{ renderDeviceLabel(d, $t("Microphone"), i) }}</option>
                                </select>
                            </div>
                            <div class="gcw-device-row" @click.stop>
                                <label class="gcw-device-label">{{ $t("Camera") }}</label>
                                <select class="gcw-device-select" :value="selectedVideoInputIndex" @change="onVideoInputDeviceChange">
                                    <option v-for="(d, i) in videoInputDevices" :key="`vin-${i}`" :value="i">{{ renderDeviceLabel(d, $t("Camera"), i) }}</option>
                                </select>
                            </div>
                            <div class="gcw-device-row" @click.stop>
                                <label class="gcw-device-label">{{ $t("Speaker") }}</label>
                                <select class="gcw-device-select" :value="selectedAudioOutputIndex" @change="onAudioOutputDeviceChange" :disabled="!supportsAudioOutputSelection || audioOutputDevices.length === 0">
                                    <option value="">{{ supportsAudioOutputSelection ? $t("Default") : $t("Not supported in this browser") }}</option>
                                    <option v-for="(d, i) in audioOutputDevices" :key="`aout-${i}`" :value="i">{{ renderDeviceLabel(d, $t("Speaker"), i) }}</option>
                                </select>
                            </div>
                        </div>
                    </Transition>

                    <!-- Click-away overlay for options menu -->
                    <div v-if="optionsMenuOpen" class="gcw-options-backdrop" @click="closeOptionsMenu"></div>
                </div>
            </div>

            <!-- RIGHT: Leave -->
            <div class="gcw-bar-right d-flex align-items-center flex-shrink-0">
                <button class="gcw-bar-btn-leave-pill rounded-pill" type="button" :title="$t('Leave Call')" :aria-label="$t('Leave Call')" @click="() => leaveCall()">
                    <i class="fas fa-phone-slash" aria-hidden="true"></i>
                </button>
            </div>
        </div>

        <!-- No-mic warning banner -->
        <div v-if="!micAvailable && joinedCall" class="gcw-mic-warning">
            <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
            <span>{{ $t("Microphone not detected") }}</span>
            <button class="gcw-mic-retry-btn rounded-pill" @click="retryMicrophone">{{ $t("Retry") }}</button>
        </div>

        </div><!-- /gcw-widget-body -->

        <!-- Sidebar panel (participants / chat) — slides in from right -->
        <Transition name="gcw-slide">
            <div v-if="panelExpanded" class="gcw-panel">

                <!-- Tabs -->
                <div class="gcw-panel-tabs d-flex flex-shrink-0" role="tablist" :aria-label="$t('Call panel tabs')">
                    <button class="gcw-tab-btn" :class="{ 'gcw-tab-active': activePanel === 'participants' }" role="tab"
                        id="gcw-tab-participants" aria-controls="gcw-panel-participants"
                        :tabindex="activePanel === 'participants' ? 0 : -1"
                        :aria-selected="activePanel === 'participants'" type="button" @click="activePanel = 'participants'">
                        <i class="fas fa-users" aria-hidden="true"></i>
                        <span>{{ $t("Participants") }}</span>
                        <span class="gcw-pill rounded-pill">{{ participantsCount }}</span>
                    </button>
                    <button class="gcw-tab-btn" :class="{ 'gcw-tab-active': activePanel === 'chat' }" role="tab"
                        id="gcw-tab-chat" aria-controls="gcw-panel-chat" :tabindex="activePanel === 'chat' ? 0 : -1"
                        :aria-selected="activePanel === 'chat'" type="button"
                        @click="activePanel = 'chat'; unreadChat = 0; $nextTick(scrollChatDown)">
                        <i class="fas fa-comment" aria-hidden="true"></i>
                        <span>{{ $t("Chat") }}</span>
                        <span v-if="unreadChat > 0" class="gcw-pill gcw-pill-red rounded-pill">{{ unreadChat > 99 ? '99+' :
                            unreadChat }}</span>
                    </button>
                </div>

                <!-- Participants tab -->
                <div v-show="activePanel === 'participants'" id="gcw-panel-participants" class="gcw-participants-list" role="tabpanel" aria-labelledby="gcw-tab-participants">
                    <div v-for="player in sortedPlayers" :key="player.uid" class="gcw-participant-row d-flex align-items-center">
                        <div class="gcw-avatar-wrap position-relative flex-shrink-0">
                            <template v-if="player.image">
                                <img class="gcw-avatar rounded-circle" :src="player.image" :alt="player.name" />
                            </template>
                            <template v-else>
                                <div class="gcw-avatar gcw-initials text-uppercase" :aria-label="player.name">{{ initials(player.name)
                                    }}</div>
                            </template>
                            <span v-if="player.audio && !player.muted" class="gcw-speaking-ring"
                                aria-hidden="true"></span>
                        </div>
                        <div class="gcw-participant-info d-flex flex-column flex-grow-1">
                            <span class="gcw-participant-name text-truncate">
                                {{ player.name }}
                                <span v-if="player.self" class="gcw-self-badge">{{ $t("You") }}</span>
                            </span>
                            <div class="gcw-status-icons d-flex">
                                <i :class="player.audio && !player.muted ? 'fas fa-microphone gcw-icon-on' : 'fas fa-microphone-slash gcw-icon-off'"
                                    aria-hidden="true"></i>
                                <i :class="player.video ? 'fas fa-video gcw-icon-on' : 'fas fa-video-slash gcw-icon-off'"
                                    aria-hidden="true"></i>
                                <i v-if="player.hand" class="fas fa-hand-paper gcw-icon-hand" aria-hidden="true"></i>
                            </div>
                        </div>
                        <div v-if="canModerate && !player.self" class="gcw-mod-actions d-flex flex-shrink-0">
                            <button class="gcw-icon-btn" :title="player.muted ? $t('Unmute') : $t('Mute')"
                                type="button" :aria-label="player.muted ? $t('Unmute') : $t('Mute')"
                                @click="player.muted ? unmuteUser(player.uid) : muteUser(player.uid)">
                                <i :class="player.muted ? 'fas fa-microphone' : 'fas fa-microphone-slash'"
                                    aria-hidden="true"></i>
                            </button>
                            <button class="gcw-icon-btn gcw-icon-btn-danger" type="button" :title="$t('Kick')" :aria-label="$t('Kick')" @click="kickUser(player.uid)">
                                <i class="fas fa-user-minus" aria-hidden="true"></i>
                            </button>
                            <button v-if="canAdmin" class="gcw-icon-btn gcw-icon-btn-danger" :title="$t('Ban')"
                                type="button" :aria-label="$t('Ban')"
                                @click="banUser(player.uid, player.name)">
                                <i class="fas fa-user-slash" aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Chat tab -->
                <div v-show="activePanel === 'chat'" id="gcw-panel-chat" class="gcw-chat-panel d-flex flex-column flex-grow-1 overflow-hidden" role="tabpanel" aria-labelledby="gcw-tab-chat">
                    <div class="gcw-chat-messages d-flex flex-column flex-grow-1" ref="chatMessagesEl">
                        <p v-if="chatMessages.length === 0" class="gcw-chat-empty m-0 text-center">{{ $t("No messages yet") }}</p>
                        <div v-for="msg in chatMessages" :key="msg.id" class="gcw-chat-entry d-flex align-items-start" :class="{
                            'gcw-chat-system': msg.type !== 'msg',
                            'gcw-chat-error': msg.type === 'error'
                        }">
                            <template v-if="msg.type === 'msg'">
                                <template v-if="msg.picture">
                                    <img class="gcw-msg-avatar rounded-circle" :src="msg.picture" :alt="msg.name" />
                                </template>
                                <template v-else>
                                    <div class="gcw-msg-avatar gcw-msg-initials text-uppercase" :aria-label="msg.name">{{
                                        initials(msg.name) }}</div>
                                </template>
                                <div class="gcw-msg-body flex-grow-1">
                                    <div class="gcw-msg-header d-flex align-items-baseline">
                                        <span class="gcw-msg-name text-truncate">{{ msg.name }}</span>
                                        <time class="gcw-msg-time">{{ renderHour(msg.time) }}</time>
                                    </div>
                                    <div class="gcw-msg-text" v-html="renderChatMessage(msg.message)"></div>
                                </div>
                            </template>
                            <template v-else>
                                <span class="gcw-system-text">{{ msg.message }}</span>
                            </template>
                        </div>
                    </div>
                    <div class="gcw-chat-footer d-flex align-items-center flex-shrink-0">
                        <input class="gcw-chat-input rounded-pill" v-model="chatInput" maxlength="300"
                            :placeholder="$t('Message...')" :aria-label="$t('Chat message')"
                            @keydown.enter.prevent="sendChatMessage" />
                        <button class="gcw-send-btn rounded-circle" :disabled="!chatInput.trim()" :title="$t('Send')"
                            type="button" :aria-label="$t('Send')"
                            @click="sendChatMessage">
                            <i class="fas fa-paper-plane" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>

            </div>
        </Transition>

        <!-- 4-corner resize handles -->
        <div class="gcw-resize-handle gcw-resize-nw" aria-hidden="true"
            @mousedown.prevent.stop="startResize('nw', $event)"
            @touchstart.prevent.stop="startResize('nw', $event)"></div>
        <div class="gcw-resize-handle gcw-resize-ne" aria-hidden="true"
            @mousedown.prevent.stop="startResize('ne', $event)"
            @touchstart.prevent.stop="startResize('ne', $event)"></div>
        <div class="gcw-resize-handle gcw-resize-sw" aria-hidden="true"
            @mousedown.prevent.stop="startResize('sw', $event)"
            @touchstart.prevent.stop="startResize('sw', $event)"></div>
        <div class="gcw-resize-handle gcw-resize-se" aria-hidden="true"
            @mousedown.prevent.stop="startResize('se', $event)"
            @touchstart.prevent.stop="startResize('se', $event)"></div>
    </div>

    <RecordingModal v-model:display="showRecordingModal" @confirm="handleRecordingAccept" @deny="handleRecordingDeny" />
    <SendRecordingModal v-model:display="showSummaryTranscriptModal" @confirm="handleSummaryTranscriptConfirm" />
    <LeaveAndSendRecordingModal v-model:display="showLeaveAndSendModal" @confirm="handleLeaveAndSendConfirm"
        @deny="handleLeaveAndSendDeny" />
    <ShareCallEmailModal v-model:display="showShareEmailModal" :roomId="roomId" :callLink="shareCallLink" />
</template>
<script lang="ts">
import { defineComponent } from "vue";
import { Request } from "@asanrom/request-browser";
import { CallsController } from "@/control/call";
import { RoomController } from "@/control/room";
import { AuthController } from "@/control/auth";
import { getUniqueStringId } from "@/utils/unique-id";
import { ActiveSpeakerTracker } from "@/control/active-speaker";

import CallPlayer from "@/components/routes/configuration/ToolsPages/CallPlayer.vue";
import RecordingModal from "@/components/modals/RecordingModal.vue";
import SendRecordingModal from "@/components/modals/SendRecordingModal.vue";
import LeaveAndSendRecordingModal from "@/components/modals/LeaveAndSendRecordingModal.vue";
import ShareCallEmailModal from "@/components/modals/ShareCallEmailModal.vue";

import { ChatController } from "@/control/chat";
import { ApiChat } from "@/api/api-group-chat";
import { ApiCalls } from "@/api/api-group-calls";
import { ApiRoomusers } from "@/api/api-group-roomusers";
import { ApiMail } from "@/api/api-group-mail";
import { ApiRoom } from "@/api/api-group-room";
import { getLanguage } from "@/i18n";
import { useControllerEvents, type EventEntry } from "@/composables/useControllerEvents";
import { resolveCallLink } from "@/utils/call-link";
import { getInitials } from "@/utils/text";

const MIN_WIDTH = 280;
const MAX_WIDTH = 640;
const MIN_HEIGHT = 300;
const MAX_HEIGHT = 500;
const DEFAULT_WIDTH = 420;
const DEFAULT_HEIGHT = 340;
const COLUMN_BREAKPOINT = 500;
const MOBILE_BREAKPOINT = 480;
const PIP_STRIP_MAX = 3;

export default defineComponent({
    name: "GlobalCallWidget",
    components: {
        CallPlayer,
        RecordingModal,
        SendRecordingModal,
        LeaveAndSendRecordingModal,
        ShareCallEmailModal,
    },
    setup() {
        return {
            requestId: getUniqueStringId(),
            loadChatHistoryRequestId: getUniqueStringId(),
            speakerTracker: null as ActiveSpeakerTracker | null,
            /** Stable reference to the active-speaker callback so it can be removed via .off(). */
            _speakerChangedCb: null as ((...a: any[]) => void) | null,
            /** Handle for the crossfade clear timeout (prevents accumulation on rapid switches). */
            _crossfadeTimeout: null as ReturnType<typeof setTimeout> | null,
        };
    },
    data() {
        return {
            inCall: true,

            // Streams and player map (kept reactive in data so Vue tracks mutations)
            streams: {} as Record<string, MediaStream>,
            playersMap: {} as Record<string, any>,

            // Widget position (free drag, no corner snapping)
            widgetLeft: -1,
            widgetTop: -1,
            isDragging: false,

            // Widget dimensions (user-resizable)
            widgetWidth: DEFAULT_WIDTH,
            widgetHeight: -1,
            isResizing: false,

            // Minimized state
            isMinimized: false,
            minimizedLeft: -1,
            minimizedTop: -1,

            // Call state
            joinedCall: false,
            callCode: "",
            selfId: "",
            audioMode: false,
            audio: true,
            video: true,
            volume: 1,
            muted: false,

            // Active speaker
            activeSpeakerId: "" as string,
            previousSpeakerId: "" as string,
            crossfadeState: "idle" as "idle" | "fading",

            // Participants
            players: [] as any[],
            participantsCount: 0,

            // Roles (updated from room-roles event)
            roomId: "",
            canAdmin: false,
            canModerate: false,

            // UI state
            panelExpanded: false,
            activePanel: "participants" as "participants" | "chat",

            // Chat
            chatMessages: [] as any[],
            chatInput: "",
            unreadChat: 0,
            chatNextId: 0,

            // Sounds
            soundSrc: "/sound/call-ding.mp3",
            soundSrcLeave: "/sound/call-leave-ding.mp3",

            // Audio context (for notification sounds)
            audioContext: null as AudioContext | null,
            analyser: null as AnalyserNode | null,
            dataArray: null as Uint8Array | null,

            // Recording
            showRecordingModal: false,
            showSummaryTranscriptModal: false,
            showLeaveAndSendModal: false,
            emailSent: false,
            tempCont: null as (() => void) | null,
            callShareMode: "public",

            // Share email modal
            showShareEmailModal: false,
            shareCallLink: "",

            // Microphone availability
            micAvailable: true,

            // Subtitles display
            recentSubtitles: [] as { txt: string; user: string }[],

            // Options menu
            optionsMenuOpen: false,
            subtitlesSubmenuOpen: false, // kept for backwards compat (unused)
            languageSubmenuOpen: false,

            // Device selectors
            audioInputDevices: [] as MediaDeviceInfo[],
            videoInputDevices: [] as MediaDeviceInfo[],
            audioOutputDevices: [] as MediaDeviceInfo[],
            selectedAudioInputIndex: "",
            selectedVideoInputIndex: "",
            selectedAudioOutputIndex: "",
            supportsAudioOutputSelection: false,

            // Hand
            hand: false,

            // Recording (own state, synced from RoomController)
            recording: false,
            recordingAdmin: "",
            busyStartRecord: false,

            // Subtitles
            subtitlesActive: RoomController.subtitlesActive,
            languageSelected: getLanguage(),
            languages: [
                { code: "es", name: "Español" },
                { code: "en", name: "English" },
            ],

            // Pin: uid of manually pinned participant, "" = auto
            pinnedUid: "",
        };
    },
    computed: {
        callTitle(): string {
            return this.callCode ? `${this.$t("In Call")} · ${this.callCode}` : this.$t("In Call");
        },
        widgetStyle(): Record<string, string | undefined> {
            if (this.widgetLeft < 0) return {};
            return {
                left: this.widgetLeft + "px",
                top: this.widgetTop + "px",
                width: this.widgetWidth + "px",
                height: this.widgetHeight > 0 ? this.widgetHeight + "px" : undefined,
            };
        },
        /** Whether the widget is narrow enough to stack in column mode. */
        isColumnMode(): boolean {
            return this.widgetWidth < COLUMN_BREAKPOINT;
        },
        minimizedStyle(): Record<string, string> {
            if (this.minimizedLeft < 0) return {};
            return {
                left: this.minimizedLeft + "px",
                top: this.minimizedTop + "px",
            };
        },
        /** The active-speaker player object (excluding self).
         *  Returns the pinned participant when a pin is set, otherwise the auto-tracked speaker. */
        activeSpeakerPlayer(): any {
            // Pin overrides auto-switch
            if (this.pinnedUid) {
                const pinned = this.players.find(p => p.uid === this.pinnedUid);
                if (pinned) return pinned;
                // Pin target left the call — treat as unpinned
            }
            if (this.activeSpeakerId) {
                const p = this.players.find(p => p.uid === this.activeSpeakerId);
                if (p) return p;
            }
            // Fallback: first remote user with video, then first remote
            const withVideo = this.players.find(p => !p.self && p.video && !p.audioMode);
            if (withVideo) return withVideo;
            const remote = this.players.find(p => !p.self);
            return remote || null;
        },

        /** The real-time speaking uid — always the raw tracker output, independent of pin.
         *  Used exclusively to show the green speaking border on PiP tiles. */
        speakingUid(): string {
            return this.activeSpeakerId;
        },
        /** Self player object. */
        selfPlayer(): any {
            return this.players.find(p => p.self) || null;
        },
        /** Whether the active speaker has video enabled. */
        speakerHasVideo(): boolean {
            const sp = this.activeSpeakerPlayer;
            return !!(sp && sp.video && !sp.audioMode && !this.audioMode && sp.streamid);
        },
        /** Whether self has video enabled. */
        selfHasVideo(): boolean {
            return !!(this.selfPlayer && this.selfPlayer.video && !this.audioMode && this.selfPlayer.streamid);
        },
        /** Group consecutive subtitle entries by speaker into blocks. */
        groupedSubtitles(): { user: string; lines: string[] }[] {
            const groups: { user: string; lines: string[] }[] = [];
            for (const sub of this.recentSubtitles) {
                const last = groups[groups.length - 1];
                if (last && last.user === sub.user) {
                    last.lines.push(sub.txt);
                } else {
                    groups.push({ user: sub.user, lines: [sub.txt] });
                }
            }
            return groups;
        },

        /** Participants sorted: self first, then active mic users, then rest. */
        sortedPlayers(): any[] {
            return [...this.players].sort((a, b) => {
                // Self always first
                if (a.self && !b.self) return -1;
                if (!a.self && b.self) return 1;
                // Active mic (audio on AND not muted) before others
                const aActive = a.audio && !a.muted ? 1 : 0;
                const bActive = b.audio && !b.muted ? 1 : 0;
                if (aActive !== bActive) return bActive - aActive;
                return 0; // keep original join order
            });
        },

        /** Remote participants shown in the PiP strip above self.
         *  Excludes self and the active speaker (already visible in the main video area).
         *  Sorted by priority: video on > speaking (audio && !muted) > idle.
         *  When widgetWidth < 350px only 1 remote slot is available (strip reduces);
         *  otherwise 2 slots (PIP_STRIP_MAX - 1). Overflow gets a "+N" chip. */
        pipStripPlayers(): any[] {
            const activeSpeakerUid = this.activeSpeakerPlayer?.uid ?? "";
            const remotes = this.players
                .filter(p => !p.self && p.uid !== activeSpeakerUid)
                .slice() // avoid mutating the reactive array
                .sort((a, b) => {
                    // Priority: 2 = has video, 1 = speaking, 0 = idle
                    const score = (p: any): number => {
                        if (p.video && !p.audioMode && !this.audioMode) return 2;
                        if (p.audio && !p.muted) return 1;
                        return 0;
                    };
                    return score(b) - score(a);
                });
            // widgetWidth is reactive — re-evaluates whenever the user resizes
            const maxRemote = this.widgetWidth < 350 ? 1 : PIP_STRIP_MAX - 1;
            const visibleCount = remotes.length > maxRemote ? maxRemote - 1 : maxRemote;
            return remotes.slice(0, visibleCount);
        },

        /** Number of remote participants hidden behind the overflow "+N" tile.
         *  Mirrors the maxRemote cap used in pipStripPlayers. */
        pipStripOverflow(): number {
            const activeSpeakerUid = this.activeSpeakerPlayer?.uid ?? "";
            const remotes = this.players.filter(p => !p.self && p.uid !== activeSpeakerUid);
            const maxRemote = this.widgetWidth < 350 ? 1 : PIP_STRIP_MAX - 1;
            if (remotes.length <= maxRemote) return 0;
            // real tiles shown = maxRemote - 1, overflow = total - that
            return remotes.length - (maxRemote - 1);
        },
    },
    methods: {
        /** Return a stable color index (0–5) for a given speaker name. */
        speakerColorIndex(name: string): number {
            let hash = 0;
            for (let i = 0; i < name.length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
            }
            return Math.abs(hash) % 6;
        },

        /** Shared API error handler — deduplicates the repeated error pattern. */
        _handleApiError(err: any, handleErr: any, extraHandlers?: Record<string, () => void>) {
            handleErr(err, {
                unauthorized: () => { this.$requireLogin(); },
                forbidden: () => { this.$showMessageModal(this.$t("Error"), this.$t("Access denied")); },
                notFound: () => { this.$showMessageModal(this.$t("Error"), this.$t("Not found")); },
                serverError: () => { this.$showMessageModal(this.$t("Error"), this.$t("Internal server error")); },
                networkError: () => { this.$showMessageModal(this.$t("Error"), this.$t("Could not connect to the server")); },
                ...extraHandlers,
            });
        },

        _handleUnexpectedError(err: any) {
            console.error(err);
            this.$showMessageModal(this.$t("Error"), err.message);
        },

        loadCallShareInfo() {
            if (!this.roomId) {
                return;
            }

            Request.Do(ApiRoom.GetRoomRoomidShare(this.roomId, {}))
                .onSuccess((shareInfo) => {
                    if (shareInfo && shareInfo.share) {
                        this.callShareMode = shareInfo.share;
                    }
                })
                .onRequestError(() => {
                    // On error, default to private-safe (hide share button until server confirms)
                    this.callShareMode = "private";
                })
                .onUnexpectedError(() => {
                    this.callShareMode = "private";
                });
        },
        copyCallLink() {
            resolveCallLink(this.roomId, (url) => {
                navigator.clipboard.writeText(url);
                this.$showSnackBar(this.$t("Call link copied to clipboard"));
            });
        },

        /** Build the call link (resolves async), then open the email share modal. */
        openShareMenu() {
            resolveCallLink(this.roomId, (url) => {
                this.shareCallLink = url;
                navigator.clipboard.writeText(url).catch(() => { /**/ });
                this.showShareEmailModal = true;
                this.closeToBottomRight();
            });
        },

        // Panel helpers
        openPanel(panel: "participants" | "chat") {
            if (this.panelExpanded && this.activePanel === panel) {
                this.panelExpanded = false;
            } else {
                this.panelExpanded = true;
                this.activePanel = panel;
                if (panel === "chat") {
                    this.unreadChat = 0;
                    this.$nextTick(() => this.scrollChatDown());
                }
            }
        },

        // Chat helpers
        sendChatMessage() {
            if (!this.chatInput.trim()) return;
            ChatController.send(this.chatInput);
            this.chatInput = "";
        },

        renderHour(ts: number): string {
            const d = new Date(ts);
            return d.getHours().toString().padStart(2, "0") + ":" + d.getMinutes().toString().padStart(2, "0");
        },

        renderChatMessage(text: string): string {
            const safe = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
            // linkify
            return safe.replace(/(https?:\/\/[^\s<>"]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
        },

        initials(name: string): string {
            return getInitials(name);
        },

        scrollChatDown() {
            const el = this.$refs.chatMessagesEl as HTMLElement;
            if (el) el.scrollTop = el.scrollHeight;
        },

        _stripChat() {
            if (this.chatMessages.length > 200) {
                this.chatMessages.splice(0, this.chatMessages.length - 200);
            }
        },

        onGcwChatMessage(event: any) {
            if (event.private) return;
            const flags = event.flags || [];
            this.chatMessages.push({
                type: "msg",
                id: event.msg_id,
                uid: event.user_id,
                name: event.user_name,
                picture: event.user_image,
                time: event.timestamp,
                mod: flags.indexOf("mod") >= 0,
                admin: flags.indexOf("admin") >= 0,
                message: event.message,
            });
            this._stripChat();
            if (this.panelExpanded && this.activePanel === "chat") {
                this.$nextTick(() => this.scrollChatDown());
            } else {
                this.unreadChat++;
            }
        },

        onGcwChatNotice(time: number, target: string, message: string, priv: boolean) {
            if (priv) return;
            this.chatMessages.push({
                type: "notice",
                id: ++this.chatNextId,
                time,
                message,
            });
            this._stripChat();
            if (this.panelExpanded && this.activePanel === "chat") {
                this.$nextTick(() => this.scrollChatDown());
            }
        },

        onGcwChatError(message: string, priv: boolean) {
            if (priv) return;
            this.chatMessages.push({
                type: "error",
                id: ++this.chatNextId,
                message,
            });
            this._stripChat();
            if (this.panelExpanded && this.activePanel === "chat") {
                this.$nextTick(() => this.scrollChatDown());
            }
        },

        onGcwChatDeleteMsg(id: number) {
            const idx = this.chatMessages.findIndex(m => m.id === id);
            if (idx >= 0) this.chatMessages.splice(idx, 1);
        },

        onGcwChatDeleteUser(uid: string) {
            this.chatMessages = this.chatMessages.filter(m => m.uid !== uid);
        },

        onGcwChatReload(roomId: string) {
            // Don't wipe existing messages while in an active call and the room ID
            // becomes empty (happens when navigating away from RoomPage mid-call).
            // The messages will be cleared once the call ends or the room changes to
            // a different non-empty room.
            if (!roomId && this.joinedCall) return;

            this.chatMessages = [];
            this.unreadChat = 0;

            if (!roomId || !AuthController.UID) return;

            Request.Pending((this as any).loadChatHistoryRequestId, ApiChat.GetRoomRoomidChatMessages(roomId, {}))
                .onSuccess((response) => {
                    const existing = new Set(this.chatMessages.map((entry) => entry.id));
                    for (const msg of response) {
                        if (msg.message.charAt(0) === "/" && msg.message.charAt(1) !== "/") {
                            continue;
                        }
                        if (existing.has(msg.id)) {
                            continue;
                        }
                        existing.add(msg.id);
                        this.chatMessages.unshift({
                            type: "msg",
                            id: msg.id,
                            uid: msg.uid,
                            name: msg.userInfo.username,
                            picture: msg.userInfo.image,
                            time: msg.timestamp,
                            mod: msg.flags.indexOf("mod") >= 0,
                            admin: msg.flags.indexOf("admin") >= 0,
                            message: msg.message,
                        });
                    }
                    this.$nextTick(() => this.scrollChatDown());
                });
        },

        // ============ Drag handlers (free position) ============
        startDrag(e: MouseEvent | TouchEvent) {
            if (this._isMobileViewport()) return;
            const target = e.target as HTMLElement | null;
            if (target?.closest("button, input, textarea, select, a, .gcw-options-menu, .gcw-chat-input, .gcw-volume-slider")) {
                return;
            }
            // Determine which element is being dragged (widget or minimized pill)
            const el = (this.isMinimized ? this.$refs.minimizedEl : this.$refs.widgetEl) as HTMLElement;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const clientX = "touches" in e ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
            const clientY = "touches" in e ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;

            (this as any)._dragState = {
                startMouseX: clientX,
                startMouseY: clientY,
                startElLeft: rect.left,
                startElTop: rect.top,
                draggingMinimized: this.isMinimized,
            };
            this.isDragging = true;

            (this as any)._onDragBound = this._onDrag.bind(this);
            (this as any)._onDragEndBound = this._onDragEnd.bind(this);
            window.addEventListener("mousemove", (this as any)._onDragBound);
            window.addEventListener("mouseup", (this as any)._onDragEndBound);
            window.addEventListener("touchmove", (this as any)._onDragBound, { passive: false });
            window.addEventListener("touchend", (this as any)._onDragEndBound);
        },

        _onDrag(e: MouseEvent | TouchEvent) {
            const ds = (this as any)._dragState;
            if (!ds) return;
            if ("touches" in e) (e as TouchEvent).preventDefault();
            const clientX = "touches" in e ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
            const clientY = "touches" in e ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;

            const el = (ds.draggingMinimized ? this.$refs.minimizedEl : this.$refs.widgetEl) as HTMLElement;
            if (!el) return;

            let newLeft = ds.startElLeft + (clientX - ds.startMouseX);
            let newTop = ds.startElTop + (clientY - ds.startMouseY);

            // Clamp within viewport
            newLeft = Math.max(0, Math.min(window.innerWidth - el.offsetWidth, newLeft));
            newTop = Math.max(0, Math.min(window.innerHeight - el.offsetHeight, newTop));

            if (ds.draggingMinimized) {
                this.minimizedLeft = newLeft;
                this.minimizedTop = newTop;
            } else {
                this.widgetLeft = newLeft;
                this.widgetTop = newTop;
            }
        },

        _onDragEnd() {
            (this as any)._dragState = null;
            window.removeEventListener("mousemove", (this as any)._onDragBound);
            window.removeEventListener("mouseup", (this as any)._onDragEndBound);
            window.removeEventListener("touchmove", (this as any)._onDragBound);
            window.removeEventListener("touchend", (this as any)._onDragEndBound);
            this.isDragging = false;
            this._saveWidgetPosition();
        },

        /** Ensure the widget stays within the viewport (called on window resize). */
        _clampToViewport() {
            if (this._isMobileViewport()) return;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            // Clamp expanded widget
            if (this.widgetLeft >= 0) {
                this.widgetLeft = Math.max(0, Math.min(vw - this.widgetWidth, this.widgetLeft));
                this.widgetTop = Math.max(0, Math.min(vh - 60, this.widgetTop));
            }
            // Clamp minimized pill — ensure it is never clipped outside the viewport
            if (this.minimizedLeft >= 0) {
                const miniEl = this.$refs.minimizedEl as HTMLElement | undefined;
                const miniW = miniEl ? miniEl.offsetWidth : 46;
                const miniH = miniEl ? miniEl.offsetHeight : 130;
                this.minimizedLeft = Math.max(0, Math.min(vw - miniW, this.minimizedLeft));
                this.minimizedTop = Math.max(0, Math.min(vh - miniH, this.minimizedTop));
            }
        },

        // ============ Resize handlers ============
        startResize(dir: 'nw' | 'ne' | 'sw' | 'se', e: MouseEvent | TouchEvent) {
            if (this._isMobileViewport()) return;
            const el = this.$refs.widgetEl as HTMLElement;
            if (!el) return;
            const clientX = "touches" in e ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
            const clientY = "touches" in e ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;
            // Anchor height if it has never been set (still auto)
            if (this.widgetHeight <= 0) {
                this.widgetHeight = el.offsetHeight;
            }

            (this as any)._resizeState = {
                dir,
                startMouseX: clientX,
                startMouseY: clientY,
                startWidth: this.widgetWidth,
                startHeight: this.widgetHeight,
                startLeft: this.widgetLeft,
                startTop: this.widgetTop,
            };
            this.isResizing = true;
            document.body.style.cursor = dir + '-resize';

            (this as any)._onResizeMoveBound = this._onResizeMove.bind(this);
            (this as any)._onResizeEndBound = this._onResizeEnd.bind(this);
            window.addEventListener("mousemove", (this as any)._onResizeMoveBound);
            window.addEventListener("mouseup", (this as any)._onResizeEndBound);
            window.addEventListener("touchmove", (this as any)._onResizeMoveBound, { passive: false });
            window.addEventListener("touchend", (this as any)._onResizeEndBound);
        },

        _onResizeMove(e: MouseEvent | TouchEvent) {
            const rs = (this as any)._resizeState;
            if (!rs) return;
            if ("touches" in e) (e as TouchEvent).preventDefault();
            const clientX = "touches" in e ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
            const clientY = "touches" in e ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;

            const dx = clientX - rs.startMouseX;
            const dy = clientY - rs.startMouseY;
            const vw = window.innerWidth;
            const vh = window.innerHeight;

            let newWidth  = rs.startWidth;
            let newHeight = rs.startHeight;
            let newLeft   = rs.startLeft;
            let newTop    = rs.startTop;

            if (rs.dir === 'se') {
                newWidth  = rs.startWidth  + dx;
                newHeight = rs.startHeight + dy;
            } else if (rs.dir === 'sw') {
                newWidth  = rs.startWidth  - dx;
                newHeight = rs.startHeight + dy;
                newLeft   = rs.startLeft   + dx;
            } else if (rs.dir === 'ne') {
                newWidth  = rs.startWidth  + dx;
                newHeight = rs.startHeight - dy;
                newTop    = rs.startTop    + dy;
            } else if (rs.dir === 'nw') {
                newWidth  = rs.startWidth  - dx;
                newHeight = rs.startHeight - dy;
                newLeft   = rs.startLeft   + dx;
                newTop    = rs.startTop    + dy;
            }

            // Clamp width
            if (newWidth < MIN_WIDTH) { newLeft += newWidth - MIN_WIDTH; newWidth = MIN_WIDTH; }
            if (newWidth > MAX_WIDTH) { newLeft += newWidth - MAX_WIDTH; newWidth = MAX_WIDTH; }
            // Clamp height
            newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, newHeight));
            // Clamp position within viewport
            newLeft = Math.max(0, Math.min(vw - newWidth, newLeft));
            newTop  = Math.max(0, Math.min(vh - MIN_HEIGHT, newTop));

            this.widgetWidth  = newWidth;
            this.widgetHeight = newHeight;
            this.widgetLeft   = newLeft;
            this.widgetTop    = newTop;
        },

        _isMobileViewport(): boolean {
            return window.innerWidth <= MOBILE_BREAKPOINT;
        },

        _onResizeEnd() {
            (this as any)._resizeState = null;
            window.removeEventListener("mousemove", (this as any)._onResizeMoveBound);
            window.removeEventListener("mouseup", (this as any)._onResizeEndBound);
            window.removeEventListener("touchmove", (this as any)._onResizeMoveBound);
            window.removeEventListener("touchend", (this as any)._onResizeEndBound);
            this.isResizing = false;
            document.body.style.cursor = '';
            this._saveWidgetPosition();
        },

        // ============ Minimize / Expand ============
        minimizeWidget() {
            // Compute minimized pill position from current widget position
            this.minimizedLeft = this.widgetLeft;
            this.minimizedTop = this.widgetTop;
            this.isMinimized = true;
            this.panelExpanded = false;
            // After render, clamp so the pill is never clipped outside the viewport
            this.$nextTick(() => {
                const el = this.$refs.minimizedEl as HTMLElement;
                if (el) {
                    this.minimizedLeft = Math.max(0, Math.min(window.innerWidth - el.offsetWidth, this.minimizedLeft));
                    this.minimizedTop = Math.max(0, Math.min(window.innerHeight - el.offsetHeight, this.minimizedTop));
                }
                this._saveWidgetPosition();
            });
        },

        /** Close button: always snaps the pill 150 px from the bottom-right corner. */
        closeToBottomRight() {
            this.isMinimized = true;
            this.panelExpanded = false;
            // Measure after render so we get real pill dimensions
            this.$nextTick(() => {
                const el = this.$refs.minimizedEl as HTMLElement;
                const h = el ? el.offsetHeight : 130;
                const w = el ? el.offsetWidth : 46;
                this.minimizedLeft = Math.max(0, window.innerWidth - w - 150);
                this.minimizedTop = Math.max(0, window.innerHeight - h - 150);
                this._saveWidgetPosition();
            });
        },

        expandWidget() {
            this.isMinimized = false;
            // Re-assign all video elements after Vue re-renders the expanded widget
            this.$nextTick(() => {
                this._clampToViewport();
                this._refreshSpeakerVideo();
                this._updateSelfVideo();
            });
            this._saveWidgetPosition();
        },

        /** Retry microphone access after initial failure. */
        async retryMicrophone() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: false,
                    audio: { channelCount: 1, sampleRate: 16000, sampleSize: 16 },
                });
                // Got mic! Replace audio track on the existing connection
                this.micAvailable = true;
                const audioTrack = stream.getAudioTracks()[0];
                if (audioTrack && CallsController.selfUserStream) {
                    // Replace existing silent/muted audio tracks on the main stream
                    const oldTracks = CallsController.selfUserStream.getAudioTracks();
                    for (const old of oldTracks) {
                        CallsController.selfUserStream.removeTrack(old);
                        old.stop();
                    }
                    CallsController.selfUserStream.addTrack(audioTrack);

                    // Also update selfUserStreamAudio (used for audio-only / audioMode peers)
                    if (CallsController.selfUserStreamAudio) {
                        const oldAudioTracks = CallsController.selfUserStreamAudio.getAudioTracks();
                        for (const old of oldAudioTracks) {
                            CallsController.selfUserStreamAudio.removeTrack(old);
                            old.stop();
                        }
                        CallsController.selfUserStreamAudio.addTrack(audioTrack.clone());
                    }

                    // Update audio on peer connections if available
                    if (CallsController.peer) {
                        const connections = (CallsController.peer as any)?.connections || {};
                        for (const peerId of Object.keys(connections)) {
                            const conns = connections[peerId];
                            for (const conn of conns) {
                                if (conn.peerConnection) {
                                    const senders = conn.peerConnection.getSenders();
                                    // Only match senders with a confirmed audio track (avoid selecting
                                    // senders with no track, which could be a video sender placeholder).
                                    const audioSender = senders.find((s: RTCRtpSender) => s.track?.kind === "audio");
                                    if (audioSender) {
                                        await audioSender.replaceTrack(audioTrack);
                                    }
                                }
                            }
                        }
                    }
                    this.$showSnackBar(this.$t("Microphone connected successfully"));
                }
            } catch (err) {
                console.warn("Retry mic failed:", err);
                this.$showSnackBar(this.$t("Microphone still not available. Please check your device."));
            }
        },

        // ============ Persistence ============
        _saveWidgetPosition() {
            try {
                localStorage.setItem("gcw-pos", JSON.stringify({
                    left: this.widgetLeft,
                    top: this.widgetTop,
                    width: this.widgetWidth,
                    height: this.widgetHeight,
                    minimized: this.isMinimized,
                    panelExpanded: this.panelExpanded,
                    activePanel: this.activePanel,
                    miniLeft: this.minimizedLeft,
                    miniTop: this.minimizedTop,
                }));
            } catch (_) { /**/ }
        },

        _loadWidgetPosition() {
            try {
                const raw = localStorage.getItem("gcw-pos");
                if (raw) {
                    const saved = JSON.parse(raw);
                    if (typeof saved.left === "number") this.widgetLeft = saved.left;
                    if (typeof saved.top === "number") this.widgetTop = saved.top;
                    if (typeof saved.width === "number") this.widgetWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, saved.width));
                    if (typeof saved.height === "number" && saved.height > 0) this.widgetHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, saved.height));
                    if (typeof saved.minimized === "boolean") this.isMinimized = saved.minimized;
                    if (saved.activePanel === "participants" || saved.activePanel === "chat") {
                        this.activePanel = saved.activePanel;
                    }
                    // Only restore panelExpanded when already in a call; otherwise keep it closed
                    if (typeof saved.panelExpanded === "boolean" && this.joinedCall) {
                        this.panelExpanded = saved.panelExpanded;
                    }
                    if (typeof saved.miniLeft === "number") this.minimizedLeft = saved.miniLeft;
                    if (typeof saved.miniTop === "number") this.minimizedTop = saved.miniTop;
                }
            } catch (_) { /**/ }
            // Default position: bottom-right with a margin
            if (this.widgetLeft < 0) {
                this.widgetLeft = window.innerWidth - this.widgetWidth - 20;
                this.widgetTop = window.innerHeight - DEFAULT_HEIGHT - 100;
            }
            if (this.minimizedLeft < 0) {
                // Default: 150 px clearance from the bottom-right edge
                // (46 px pill width, ~130 px estimated pill height)
                this.minimizedLeft = Math.max(0, window.innerWidth - 46 - 150);
                this.minimizedTop = Math.max(0, window.innerHeight - 130 - 150);
            }
            if (this.isMinimized) {
                this.panelExpanded = false;
            }
            this._clampToViewport();
            this.$nextTick(() => {
                this.isDragging = false;
            });
        },

        // ============ Active speaker video management ============
        _onActiveSpeakerChanged(uid: string, prevUid: string) {
            this.previousSpeakerId = prevUid;
            this.activeSpeakerId = uid;
            // Only switch the main video when no pin is active
            if (!this.pinnedUid) {
                this._updateMainVideo(uid, prevUid);
            }
        },

        /** Toggle pin on a participant.
         *  Pinning locks the main video on that person regardless of who is speaking.
         *  Clicking pin again (or the same uid) removes the pin and restores auto-switch. */
        pinPlayer(uid: string) {
            if (this.pinnedUid === uid) {
                // Unpin — restore the current auto-tracked speaker
                this.pinnedUid = "";
                this._updateMainVideo(this.activeSpeakerId, uid);
            } else {
                const prev = this.activeSpeakerPlayer?.uid ?? "";
                this.pinnedUid = uid;
                this._updateMainVideo(uid, prev);
            }
        },

        _updateMainVideo(newUid: string, prevUid: string) {
            const mainVideo = this.$refs.mainVideo as HTMLVideoElement;
            const prevVideo = this.$refs.mainVideoPrev as HTMLVideoElement;
            if (!mainVideo) return;

            // Get new speaker stream
            const newPlayer = this.playersMap[newUid];
            const newStream = newPlayer?.streamid ? this.streams[newPlayer.streamid] : null;

            if (prevUid && prevVideo && mainVideo.srcObject) {
                // Crossfade: move current to prev, assign new to main
                prevVideo.srcObject = mainVideo.srcObject;
                this.crossfadeState = "fading";
                if ((this as any)._crossfadeTimeout) {
                    clearTimeout((this as any)._crossfadeTimeout);
                }
                (this as any)._crossfadeTimeout = setTimeout(() => {
                    this.crossfadeState = "idle";
                    if (prevVideo) prevVideo.srcObject = null;
                    (this as any)._crossfadeTimeout = null;
                }, 350);
            }

            if (newStream) {
                mainVideo.srcObject = newStream;
                mainVideo.play().catch(() => { /**/ });
            } else {
                mainVideo.srcObject = null;
            }
        },

        _updateSelfVideo() {
            const selfVideo = this.$refs.selfVideo as HTMLVideoElement;
            if (!selfVideo) return;
            const sp = this.selfPlayer;
            if (!sp || !sp.streamid) {
                selfVideo.srcObject = null;
                return;
            }
            const stream = this.streams[sp.streamid];
            if (stream && selfVideo.srcObject !== stream) {
                selfVideo.srcObject = stream;
                selfVideo.play().catch(() => { /**/ });
            }
        },

        /** Assign a remote participant's stream to a pip-strip video element.
         *  Called via Vue function-ref on each strip tile — fires on every render. */
        _assignStripVideo(el: HTMLVideoElement | null, player: any) {
            if (!el) return;
            const stream = player?.streamid ? this.streams[player.streamid] : null;
            if (stream && el.srcObject !== stream) {
                el.srcObject = stream;
                el.play().catch(() => { /**/ });
            } else if (!stream) {
                el.srcObject = null;
            }
        },

        /** Re-assign main video when streams change. */
        _refreshSpeakerVideo() {
            const sp = this.activeSpeakerPlayer;
            const mainVideo = this.$refs.mainVideo as HTMLVideoElement;
            if (!sp) {
                if (mainVideo) mainVideo.srcObject = null;
                return;
            }
            if (!mainVideo) return;
            const stream = sp.streamid ? this.streams[sp.streamid] : null;
            if (stream && mainVideo.srcObject !== stream) {
                mainVideo.srcObject = stream;
                mainVideo.play().catch(() => { /**/ });
            } else if (!stream) {
                mainVideo.srcObject = null;
            }
        },

        // Navigation helpers
        navigateToCall() {
            const rid = CallsController.room || this.roomId;
            if (rid) {
                this.$router.push({
                    name: "section",
                    params: { roomId: rid, sectionId: "call" },
                });
            }
        },

        // Self A/V controls
        toggleAudio() {
            CallsController.enableAudio(!this.audio);
        },

        toggleVideo() {
            if (!this.audioMode) {
                CallsController.enableVideo(!this.video);
            }
        },

        // ============ Options menu ============
        toggleOptionsMenu() {
            this.optionsMenuOpen = !this.optionsMenuOpen;
            if (this.optionsMenuOpen) {
                this.refreshMediaDevices();
            }
            if (!this.optionsMenuOpen) {
                this.languageSubmenuOpen = false;
            }
        },

        closeOptionsMenu() {
            this.optionsMenuOpen = false;
            this.languageSubmenuOpen = false;
        },

        onGlobalKeyDown(event: KeyboardEvent) {
            if (event.key !== "Escape") return;
            if (!this.optionsMenuOpen) return;
            this.closeOptionsMenu();
        },

        async refreshMediaDevices() {
            if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                this.audioInputDevices = devices.filter(d => d.kind === "audioinput");
                this.videoInputDevices = devices.filter(d => d.kind === "videoinput");
                this.audioOutputDevices = devices.filter(d => d.kind === "audiooutput");

                const currentAudioInput = CallsController.getPreferredAudioInputDevice();
                const currentVideoInput = CallsController.getPreferredVideoInputDevice();
                const audioIndex = this.audioInputDevices.findIndex(d => d.deviceId === currentAudioInput);
                const videoIndex = this.videoInputDevices.findIndex(d => d.deviceId === currentVideoInput);
                this.selectedAudioInputIndex = audioIndex >= 0 ? String(audioIndex) : (this.audioInputDevices.length ? "0" : "");
                this.selectedVideoInputIndex = videoIndex >= 0 ? String(videoIndex) : (this.videoInputDevices.length ? "0" : "");

                const sinkProbe = document.createElement("audio") as any;
                this.supportsAudioOutputSelection = typeof sinkProbe.setSinkId === "function";
            } catch (err) {
                console.error("Failed to enumerate media devices:", err);
            }
        },

        renderDeviceLabel(device: MediaDeviceInfo, fallback: string, index: number): string {
            const label = (device?.label || "").trim();
            if (!label) {
                return fallback + " " + (index + 1);
            }
            // Browsers often append hardware IDs like "(046d:0a45)".
            // Strip those tokens so we don't expose device identifiers in UI.
            const sanitized = label
                .replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)\s*$/i, "")
                .replace(/\s*\([0-9a-f]{4,}\)\s*$/i, "")
                .trim();
            return sanitized || (fallback + " " + (index + 1));
        },

        async onAudioInputDeviceChange(event: Event) {
            const indexVal = ((event.target as HTMLSelectElement)?.value || "").trim();
            this.selectedAudioInputIndex = indexVal;
            const idx = Number(indexVal);
            const deviceId = Number.isFinite(idx) && this.audioInputDevices[idx] ? this.audioInputDevices[idx].deviceId : "";
            try {
                await CallsController.switchAudioInputDevice(deviceId);
                this.$showSnackBar(this.$t("Microphone connected successfully"));
            } catch (err: any) {
                console.error("Failed to switch audio input:", err);
                this.$showMessageModal(this.$t("Error"), this.$t("Error accessing the microphone"));
            }
        },

        async onVideoInputDeviceChange(event: Event) {
            const indexVal = ((event.target as HTMLSelectElement)?.value || "").trim();
            this.selectedVideoInputIndex = indexVal;
            const idx = Number(indexVal);
            const deviceId = Number.isFinite(idx) && this.videoInputDevices[idx] ? this.videoInputDevices[idx].deviceId : "";
            try {
                await CallsController.switchVideoInputDevice(deviceId);
            } catch (err: any) {
                console.error("Failed to switch video input:", err);
                this.$showMessageModal(this.$t("Error"), this.$t("The camera could not be accessed."));
            }
        },

        async onAudioOutputDeviceChange(event: Event) {
            const indexVal = ((event.target as HTMLSelectElement)?.value || "").trim();
            this.selectedAudioOutputIndex = indexVal;
            const idx = Number(indexVal);
            const deviceId = Number.isFinite(idx) && this.audioOutputDevices[idx] ? this.audioOutputDevices[idx].deviceId : "";
            try {
                await this.applyAudioOutputDevice(deviceId);
            } catch (err) {
                console.error("Failed to switch audio output:", err);
            }
        },

        async applyAudioOutputDevice(deviceId: string) {
            const sinkId = (deviceId || "").trim();
            const els: any[] = [];
            const roomSound = this.$refs.roomSound as any;
            const roomSoundLeave = this.$refs.roomSoundLeave as any;
            if (roomSound) els.push(roomSound);
            if (roomSoundLeave) els.push(roomSoundLeave);

            // Best-effort: call audio is routed via WebAudio in CallPlayer,
            // but notification sounds and any HTMLMediaElements can be moved.
            const videos = this.$el?.querySelectorAll?.("video") || [];
            videos.forEach((v: any) => els.push(v));

            for (const el of els) {
                if (typeof el.setSinkId === "function") {
                    await el.setSinkId(sinkId);
                }
            }
        },

        // ============ Raise hand ============
        toggleHand() {
            this.hand = !this.hand;
            CallsController.raiseHand(this.hand);
            this.$showSnackBar(this.hand ? this.$t("You raised your hand!") : this.$t("You lowered your hand!"));
        },

        // ============ Subtitles ============
        onRoomSubtitlesChanged(payload: { active?: boolean }) {
            const active = !!payload?.active;
            this.subtitlesActive = active;
            if (!active) {
                this.recentSubtitles = [];
                this.languageSubmenuOpen = false;
            }
        },

        handleSubtitlesClick() {
            if (!this.subtitlesActive) {
                // Activate subtitles and open the language flyout
                CallsController.lan = this.languageSelected;
                RoomController.setSubtitlesActive(true, "global-call-widget");
                this.subtitlesActive = true;
                this.languageSubmenuOpen = true;
            } else {
                // Toggle language flyout
                this.languageSubmenuOpen = !this.languageSubmenuOpen;
            }
        },

        toggleSubtitles() {
            if (this.subtitlesActive) {
                this.subtitlesActive = false;
                RoomController.setSubtitlesActive(false, "global-call-widget");
                this.recentSubtitles = [];
                this.languageSubmenuOpen = false;
            } else {
                CallsController.lan = this.languageSelected;
                RoomController.setSubtitlesActive(true, "global-call-widget");
                this.subtitlesActive = true;
            }
        },

        selectSubtitleLanguage(code: string) {
            if (code !== this.languageSelected) {
                this.languageSelected = code;
                CallsController.lan = code;
                CallsController.changeLanguage();
            }
            // Auto-enable subtitles on language pick
            if (!this.subtitlesActive) {
                CallsController.lan = code;
                RoomController.setSubtitlesActive(true, "global-call-widget");
                this.subtitlesActive = true;
            }
        },

        // ============ Recording (from options menu) ============
        startRecord() {
            this.closeOptionsMenu();
            this.$askUserConfirmation({
                title: this.$t("Start recording"),
                message: this.$t("Are you sure you want to start recording? All users will be informed"),
                callback: () => {
                    if (this.busyStartRecord) return;
                    this.busyStartRecord = true;
                    Request.Do(ApiCalls.StartCallRecord(this.roomId))
                        .onSuccess((response: any) => {
                            this.recordingAdmin = response.admin;
                            this.recording = true;
                            CallsController.recordID = response.id;
                            this.busyStartRecord = false;
                            RoomController.startRecord(this.roomId, response.admin, response.id);
                            this.$showSnackBar(this.$t("Recording has started"));
                        })
                        .onRequestError((err: any, handleErr: any) => {
                            this.busyStartRecord = false;
                            this._handleApiError(err, handleErr, {
                                badRequestCallAlreadyRecording: () => { this.$showMessageModal(this.$t("Error"), this.$t("Call is already being recorded")); },
                            });
                        })
                        .onUnexpectedError((err: any) => {
                            this.busyStartRecord = false;
                            this._handleUnexpectedError(err);
                        });
                },
            });
        },

        stopRecordFromMenu() {
            this.closeOptionsMenu();
            this.stopRecord();
        },

        // ============ Raised hand notification ============
        onRaisedHand(data: { uid: string; name: string }) {
            // The event fires for both raise & lower — only notify on raise.
            // At this point the player's hand flag hasn't been updated yet by user-change,
            // so hand===false means the user is RAISING it now.
            const player = this.playersMap[data.uid] || this.players.find((p: any) => p.uid === data.uid);
            const isRaising = player ? !player.hand : true;
            if (isRaising) {
                const name = (player && player.name) || data.name || "?";
                this.$showSnackBar(name + " " + this.$t("raised the hand!"));
            }
        },

        // ============ Subtitles received ============
        onTranslationReceived(payload: { callId: string; messages: { name: string; txt: string }[] }) {
            const messages = payload?.messages || [];
            if (payload?.callId !== (CallsController.callId || "")) return;
            if (!this.subtitlesActive) return;
            if (!messages || !messages.length) return;

            this.recentSubtitles = messages.map(m => ({ txt: m.txt, user: m.name }));

            // After render, measure actual pixel heights and trim grouped blocks to fit
            const maxHeight = 130;
            this.$nextTick(() => {
                const container = this.$refs.subtitlesContainer as HTMLElement | undefined;
                if (!container) return;
                const blocks = container.querySelectorAll('.gcw-subtitle-line');
                if (blocks.length > 0 && container.scrollHeight > maxHeight) {
                    // Walk from the last block backwards, keep blocks that fit
                    let totalHeight = 0;
                    let firstVisibleBlock = blocks.length;
                    for (let i = blocks.length - 1; i >= 0; i--) {
                        const blockHeight = (blocks[i] as HTMLElement).getBoundingClientRect().height;
                        if (totalHeight + blockHeight <= maxHeight) {
                            totalHeight += blockHeight;
                            firstVisibleBlock = i;
                        } else {
                            break;
                        }
                    }
                    // Trim recentSubtitles so only the messages belonging to visible groups remain
                    if (firstVisibleBlock > 0) {
                        const groups = this.groupedSubtitles;
                        // Count how many raw entries the trimmed groups cover
                        let keepCount = 0;
                        for (let g = firstVisibleBlock; g < groups.length; g++) {
                            keepCount += groups[g].lines.length;
                        }
                        if (keepCount < this.recentSubtitles.length) {
                            this.recentSubtitles = this.recentSubtitles.slice(-keepCount);
                        }
                    }
                }
            });
        },

        // ============ Volume (from options menu) ============
        toggleMuted() {
            this.muted = !this.muted;
            if (this.muted) {
                this.volume = 0;
            } else {
                this.volume = 1;
            }
            RoomController.updateVolume(this.volume);
        },

        onVolumeInput(e: Event) {
            const val = parseInt((e.target as HTMLInputElement).value, 10);
            this.volume = val / 100;
            this.muted = val === 0;
            RoomController.updateVolume(this.volume);
        },

        // CallsController event handlers
        onOpenCall(uid: string, stream: MediaStream, userInfo: { name: string; image: string }, muted: boolean, audioMode: boolean, callEvent?: any) {
            // Reset speaker state in case of rejoin without a clean close
            this.callCode = callEvent?.call_code || "";
            this.recentSubtitles = [];
            this.activeSpeakerId = "";
            this.previousSpeakerId = "";
            this.crossfadeState = "idle";
            if ((this as any)._crossfadeTimeout) {
                clearTimeout((this as any)._crossfadeTimeout);
                (this as any)._crossfadeTimeout = null;
            }

            this.selfId = uid;
            this.audioMode = audioMode;
            this.streams[stream.id] = stream;
            this.video = CallsController.video;
            if (RoomController.recording || CallsController.recording || callEvent?.status === true) {
                this.recording = true;
                this.recordingAdmin = RoomController.recordingUser || CallsController.adminRecord;
            }

            // Guard against duplicate self entry (e.g. rejoin without a clean close event)
            if (this.playersMap[uid]) {
                this.players = this.players.filter(p => p.uid !== uid);
                delete this.playersMap[uid];
            }

            this.players.push({
                self: true,
                uid,
                name: userInfo.name,
                image: userInfo.image,
                audio: this.audio,
                video: this.video,
                hand: false,
                muted,
                audioMode,
                streamid: stream.id,
                lastSound: 0,
                stick: 0,
                volume: 1,
            });
            this.playersMap[uid] = this.players[this.players.length - 1];
            this.participantsCount = this.players.length;
            document.body.classList.add("in-call");

            // Initialize speaker tracker
            this.speakerTracker = new ActiveSpeakerTracker(uid);
            (this as any)._speakerChangedCb = this._onActiveSpeakerChanged.bind(this);
            this.speakerTracker.on((this as any)._speakerChangedCb);

            // Assign self video
            this.$nextTick(() => this._updateSelfVideo());
        },

        joined() {
            this.joinedCall = true;
            this.playSound("join");
        },

        onCloseCall(transition?: { preserveRecording?: boolean; preserveSubtitles?: boolean }) {
            const preserveRecording = !!transition?.preserveRecording
                && (RoomController.recording || CallsController.recording);
            const preserveSubtitles = !!transition?.preserveSubtitles;
            this.joinedCall = false;
            this.callCode = "";
            // If the user navigated away from the room page while in the call,
            // RoomController.room is now empty — clear the preserved roomId.
            if (!RoomController.room) {
                this.roomId = "";
            }
            this.micAvailable = true;
            this.players = [];
            this.streams = {};
            this.playersMap = {};
            this.participantsCount = 0;
            this.panelExpanded = false;
            this.activeSpeakerId = "";
            this.previousSpeakerId = "";
            this.callShareMode = "public";
            document.body.classList.remove("in-call");
            this.playSound("leave");

            // Reset options menu state
            this.optionsMenuOpen = false;
            this.languageSubmenuOpen = false;
            this.hand = false;
            if (!preserveRecording) {
                this.recording = false;
                this.recordingAdmin = "";
            }
            this.busyStartRecord = false;
            this.recentSubtitles = [];
            if (!preserveSubtitles) {
                this.subtitlesActive = false;
                RoomController.setSubtitlesActive(false, "global-call-widget-close");
            }

            // Sync RoomController
            RoomController.onLeaveCall();

            // Destroy speaker tracker
            if (this.speakerTracker) {
                if ((this as any)._speakerChangedCb) {
                    this.speakerTracker.off((this as any)._speakerChangedCb);
                    (this as any)._speakerChangedCb = null;
                }
                this.speakerTracker.destroy();
                this.speakerTracker = null;
            }

            // Cancel any in-progress crossfade
            if ((this as any)._crossfadeTimeout) {
                clearTimeout((this as any)._crossfadeTimeout);
                (this as any)._crossfadeTimeout = null;
            }
            this.crossfadeState = "idle";

            // Clear video elements
            const mainVideo = this.$refs.mainVideo as HTMLVideoElement;
            const selfVideo = this.$refs.selfVideo as HTMLVideoElement;
            if (mainVideo) mainVideo.srcObject = null;
            if (selfVideo) selfVideo.srcObject = null;
        },

        onCallError(title: string, message: string) {
            this.$showMessageModal(this.$t(title), this.$t(message));
        },

        onUserJoin(uid: string, userInfo: { name: string; image: string }, muted: boolean, video: boolean, audio: boolean, flags: string[], hand: boolean, audioMode: boolean) {
            // Guard against duplicates (e.g. fast disconnect → reconnect emits user-leave then user-join,
            // but a stale push from a previous close may still be in-flight)
            if (this.playersMap[uid]) return;

            const newUser = {
                self: false,
                uid,
                name: userInfo.name,
                image: userInfo.image,
                audio,
                video,
                hand,
                muted,
                audioMode,
                streamid: "",
                lastSound: 0,
                stick: 0,
                volume: 1,
            };
            this.players.push(newUser);
            this.playersMap[uid] = this.players[this.players.length - 1];
            this.participantsCount = this.players.length;
            RoomController.onUserJoin(uid, newUser);
            RoomController.players = this.players;
            this.playSound("join");

            // Update speaker tracker video flag
            if (this.speakerTracker) {
                this.speakerTracker.setVideoFlag(uid, video && !audioMode);
            }
        },

        onUserLeave(id: string) {
            const player = this.players.find(p => p.uid === id);
            if (player) {
                // Clean streams
                if (player.streamid) {
                    delete this.streams[player.streamid];
                }

                // Clear pin if the pinned participant left
                if (this.pinnedUid === id) {
                    this.pinnedUid = "";
                }

                // Remove from local state
                this.players = this.players.filter(p => p.uid !== id);
                delete this.playersMap[id];
                this.participantsCount = this.players.length;

                // Sync RoomController
                RoomController.onUserLeave(id);
                RoomController.players = this.players;

                this.playSound("leave");

                // Remove from speaker tracker
                if (this.speakerTracker) {
                    this.speakerTracker.removeStream(id);
                }

                // If this was the active speaker, clear and refresh
                if (this.activeSpeakerId === id) {
                    this.activeSpeakerId = "";
                    this.$nextTick(() => this._refreshSpeakerVideo());
                }
            }
        },

        onUserChange(id: string, muted: boolean, video: boolean, audio: boolean, hand: boolean) {
            if (this.playersMap[id]) {
                this.playersMap[id].muted = muted;
                this.playersMap[id].video = video;
                this.playersMap[id].audio = audio;
                this.playersMap[id].hand = hand;
                RoomController.playersMap[id] = this.playersMap[id];

                // Update video flag for speaker priority
                if (this.speakerTracker) {
                    this.speakerTracker.setVideoFlag(id, video && !this.playersMap[id].audioMode);
                }

                // Splice the player back into the reactive array so that Vue computeds
                // that sort/filter by video/audio/muted (e.g. pipStripPlayers) re-evaluate.
                // Use a spread copy so Vue detects a new object reference, then re-sync
                // playersMap so future mutations still target the live array element.
                const idx = this.players.findIndex(p => p.uid === id);
                if (idx >= 0) {
                    this.players.splice(idx, 1, { ...this.players[idx] });
                    this.playersMap[id] = this.players[idx];
                    RoomController.playersMap[id] = this.players[idx];
                }

                // Refresh video if speaker changed their cam
                if (id === this.activeSpeakerId || id === this.pinnedUid || (this.selfPlayer && id === this.selfPlayer.uid)) {
                    this.$nextTick(() => {
                        this._refreshSpeakerVideo();
                        this._updateSelfVideo();
                    });
                }
            }
        },

        onUserStream(id: string, stream: MediaStream | null) {
            if (stream) {
                this.streams[stream.id] = stream;
                RoomController.savedStreams[stream.id] = stream;
                if (this.playersMap[id]) {
                    this.playersMap[id].streamid = stream.id;
                    this.playersMap[id].stick++;
                }
                if (RoomController.recording && RoomController.recordingUser === this.selfId) {
                    CallsController.startRecordingForUser(id, stream, CallsController.recordID);
                }

                // Add to speaker tracker for volume analysis
                if (this.speakerTracker && id !== this.selfId) {
                    this.speakerTracker.addStream(id, stream);
                }

                // Splice to trigger reactivity so pip-strip re-renders with the new streamid.
                // Re-sync playersMap so future mutations target the live array element.
                const sidxNew = this.players.findIndex(p => p.uid === id);
                if (sidxNew >= 0) {
                    this.players.splice(sidxNew, 1, { ...this.players[sidxNew] });
                    this.playersMap[id] = this.players[sidxNew];
                }

                // Refresh video elements
                this.$nextTick(() => {
                    if (this.selfPlayer && id === this.selfPlayer.uid) {
                        this._updateSelfVideo();
                    }
                    if (!this.activeSpeakerId || id === this.activeSpeakerId || id === this.pinnedUid) {
                        this._refreshSpeakerVideo();
                    }
                    // If no speaker yet and this is a remote user, pick them
                    if (!this.activeSpeakerId && id !== this.selfId) {
                        this.activeSpeakerId = id;
                        this._refreshSpeakerVideo();
                    }
                });
            } else {
                // Null stream means the peer disconnected
                if (this.playersMap[id]) {
                    const oldStreamId = this.playersMap[id].streamid;
                    if (oldStreamId) {
                        delete this.streams[oldStreamId];
                        delete RoomController.savedStreams[oldStreamId];
                    }
                    this.playersMap[id].streamid = "";
                    this.playersMap[id].stick++;
                }

                // Splice to clear pip-strip video when stream is gone.
                // Re-sync playersMap so future mutations target the live array element.
                const sidxNull = this.players.findIndex(p => p.uid === id);
                if (sidxNull >= 0) {
                    this.players.splice(sidxNull, 1, { ...this.players[sidxNull] });
                    this.playersMap[id] = this.players[sidxNull];
                }

                // Remove from tracker
                if (this.speakerTracker) {
                    this.speakerTracker.removeStream(id);
                }

                // Refresh main video if this was the active speaker
                if (id === this.activeSpeakerId) {
                    this.$nextTick(() => this._refreshSpeakerVideo());
                }
            }
        },

        onUserVolume(id: string, volume: number) {
            if (this.playersMap[id]) {
                this.playersMap[id].lastSound = volume;
            }
        },

        onSelfChange(video: boolean, audio: boolean, hand: boolean) {
            this.video = video;
            this.audio = audio;
            this.hand = hand;
            // Splice the self-player with updated values so Vue computeds and the
            // participants list react to mic/cam/hand changes for the local user.
            const selfIdx = this.players.findIndex(p => p.self);
            if (selfIdx >= 0) {
                const updated = { ...this.players[selfIdx], video, audio, hand };
                this.players.splice(selfIdx, 1, updated);
                this.playersMap[this.selfId] = this.players[selfIdx];
            }
            this.$nextTick(() => this._updateSelfVideo());
        },

        onStreamRequest(player: any, streamid: string) {
            if (this.streams?.[streamid]) {
                player.setStream(this.streams[streamid]);
            }
        },

        // RoomController event handlers
        onRoomRoles(id: string, roles: any) {
            // While the user is still in an active call, keep the room ID so that
            // moderation, recording, and share-link API calls continue to work even
            // after navigating away from RoomPage.
            if (id || !this.joinedCall) {
                this.roomId = id;
            }
            this.canAdmin = !!roles.admin;
            this.canModerate = !!roles.mod;
            this.loadCallShareInfo();
        },

        /**
         * Called when the RoomController emits 'stop-record'.
         * Only updates local UI state — does NOT re-trigger the stop flow.
         */
        onStopRecordEvent() {
            this.recording = false;
            this.recordingAdmin = "";
            this.busyStartRecord = false;
        },

        stopRecord: async function () {
            RoomController.recording = false;
            this.recording = false;
            this.recordingAdmin = "";
            await CallsController.criticalSection.acquire();
            CallsController.stopRecord(CallsController.recordID, this.selfId);
            RoomController.stopRecord();
            if (this.roomId) {
                CallsController.askStopRecording(this.roomId);
                CallsController.askSummary(this.roomId);
            }
        },

        stopRecordUser() {
            RoomController.recording = false;
            this.recording = false;
            this.recordingAdmin = "";
            if (!this.emailSent && !this.showSummaryTranscriptModal && !AuthController.isAnon()) {
                this.showSummaryTranscriptModal = true;
            }
        },

        stopRecordOnLeave: async function () {
            await CallsController.criticalSection.acquire();
            CallsController.stopRecord(CallsController.recordID, this.selfId);
            RoomController.stopRecord();
            this.recording = false;
            this.recordingAdmin = "";
        },

        onLeaveWhileRecording(cont?: () => void) {
            this.leaveCall(typeof cont === "function" ? cont : undefined);
        },

        // Leave call
        leaveCall(cont?: () => void) {
            if (RoomController.recording && !this.emailSent) {
                this.showLeaveAndSendModal = true;
                this.tempCont = typeof cont === "function" ? cont : null;
                return;
            }
            this.$askUserConfirmation({
                title: this.$t("Leave the call?"),
                message: this.$t("You are about to leave the call. Are you sure?"),
                icon: "fa-solid fa-phone-slash",
                callback: () => {
                    CallsController.cancelBreakoutSwitch();
                    if (typeof cont === "function") {
                        CallsController.leaveCall(cont);
                    } else {
                        CallsController.leaveCall();
                    }
                },
            });
        },

        // Recording modals
        onAskRecording() {
            this.emailSent = false;
            this.showRecordingModal = true;
            // Do NOT set this.recording = true here — recording hasn't started yet.
            // It will be set to true in handleRecordingAccept() after the user confirms.
        },

        handleRecordingAccept() {
            this.showRecordingModal = false;
            RoomController.recording = true;
            this.recording = true;
            this.recordingAdmin = RoomController.recordingUser;
            CallsController.startRecordingUser = true;
            if (!RoomController.joinedCall) {
                RoomController.handleRecordingConfirm();
            }
        },

        handleRecordingDeny() {
            CallsController.cancelBreakoutSwitch();
            CallsController.leaveCall();
        },

        handleLeaveAndSendConfirm(sendTranscription: boolean, sendSummary: boolean) {
            this.emailSent = true;
            let u: string | undefined;
            for (const p of this.players) {
                if (p.self) { u = p.uid; }
            }
            if (RoomController.recording) {
                if (u === RoomController.recordingUser) {
                    this.stopRecordOnLeave();
                } else {
                    this.stopRecordUser();
                }
            }
            if (sendTranscription || sendSummary) {
                Request.Do(ApiMail.SendCallTranscriptionAndSummaryEmail({
                    crid: CallsController.recordID,
                    rid: this.roomId,
                    sendTranscription,
                    sendSummary,
                }))
                    .onSuccess(() => {
                        this.$showSnackBar(this.$t("Shortly, you will receive an email"));
                        CallsController.cancelBreakoutSwitch();
                        CallsController.leaveCall(this.tempCont);
                    })
                    .onRequestError((err, handleErr) => {
                        this._handleApiError(err, handleErr, {
                            notFound: () => { this.$showMessageModal(this.$t("Error"), this.$t("Room not found")); },
                            notFoundRecordNotFound: () => { this.$showMessageModal(this.$t("Error"), this.$t("Recording not found")); },
                            notFoundRoomNotFound: () => { this.$showMessageModal(this.$t("Error"), this.$t("Room not found")); },
                        });
                    })
                    .onUnexpectedError((err) => { this._handleUnexpectedError(err); });
            } else {
                CallsController.cancelBreakoutSwitch();
                CallsController.leaveCall(this.tempCont);
            }
        },

        handleLeaveAndSendDeny() {
            this.showLeaveAndSendModal = false;
            this.tempCont = null;
        },

        handleSummaryTranscriptConfirm(sendTranscription: boolean, sendSummary: boolean) {
            if (this.emailSent) { return; }
            Request.Do(ApiMail.SendCallTranscriptionAndSummaryEmail({
                crid: CallsController.recordID,
                rid: this.roomId,
                sendTranscription,
                sendSummary,
            }))
                .onSuccess(() => {
                    this.emailSent = true;
                    this.showSummaryTranscriptModal = false;
                    this.$showSnackBar(this.$t("Shortly, you will receive an email"));
                })
                .onRequestError((err, handleErr) => {
                    this._handleApiError(err, handleErr, {
                        notFound: () => { this.$showMessageModal(this.$t("Error"), this.$t("Room not found")); },
                        notFoundRecordNotFound: () => { this.$showMessageModal(this.$t("Error"), this.$t("Recording not found")); },
                        notFoundRoomNotFound: () => { this.$showMessageModal(this.$t("Error"), this.$t("Room not found")); },
                    });
                })
                .onUnexpectedError((err) => { this._handleUnexpectedError(err); });
        },

        onAskSummary() {
            if (this.joinedCall && !this.showSummaryTranscriptModal && !this.emailSent) {
                this.stopRecordUser();
            }
        },

        // Moderation
        muteUser(uid: string) {
            Request.Do(ApiCalls.PostRoomRoomidCallMute(this.roomId, { uid, callId: CallsController.callId }))
                .onSuccess(() => { this.$showSnackBar(this.$t("User successfully muted")); })
                .onRequestError((err, handleErr) => {
                    this._handleApiError(err, handleErr, {
                        badRequest: () => { this.$showMessageModal(this.$t("Error"), this.$t("User was already muted")); },
                    });
                })
                .onUnexpectedError((err) => { this._handleUnexpectedError(err); });
        },

        unmuteUser(uid: string) {
            Request.Do(ApiCalls.PostRoomRoomidCallUnmute(this.roomId, { uid, callId: CallsController.callId }))
                .onSuccess(() => { this.$showSnackBar(this.$t("User successfully unmuted")); })
                .onRequestError((err, handleErr) => {
                    this._handleApiError(err, handleErr, {
                        badRequest: () => { this.$showMessageModal(this.$t("Error"), this.$t("User was not muted")); },
                    });
                })
                .onUnexpectedError((err) => { this._handleUnexpectedError(err); });
        },

        kickUser(uid: string) {
            Request.Do(ApiCalls.PostRoomRoomidCallKick(this.roomId, { uid, callId: CallsController.callId }))
                .onSuccess(() => { this.$showSnackBar(this.$t("User successfully kicked from the call")); })
                .onRequestError((err, handleErr) => { this._handleApiError(err, handleErr); })
                .onUnexpectedError((err) => { this._handleUnexpectedError(err); });
        },

        banUser(uid: string, name: string) {
            this.$askUserConfirmation({
                title: this.$t("Ban user"),
                message: this.$t("Do you want to ban this user?") + " - " + name,
                icon: "fa-solid fa-user-slash",
                callback: () => {
                    Request.Do(ApiRoomusers.PostRoomRoomidUserUseridBan(this.roomId, uid))
                        .onSuccess(() => { this.$showSnackBar(this.$t("User successfully banned")); })
                        .onRequestError((err, handleErr) => {
                            this._handleApiError(err, handleErr, {
                                badRequest: () => { this.$showMessageModal(this.$t("Error"), this.$t("User not found")); },
                            });
                        })
                        .onUnexpectedError((err) => { this._handleUnexpectedError(err); });
                },
            });
        },

        // Sound helpers
        async playSound(type: "join" | "leave") {
            try {
                const soundConfig = {
                    join: { ref: "roomSound", src: this.soundSrc },
                    leave: { ref: "roomSoundLeave", src: this.soundSrcLeave },
                };
                const config = soundConfig[type];
                const audioEl = this.$refs[config.ref] as HTMLAudioElement;
                if (!audioEl) return;
                if (!this.audioContext) {
                    this.setupAudioContext(audioEl);
                }
                await audioEl.play();
            } catch (error) {
                console.warn(`Error playing ${type} sound:`, error);
            }
        },

        setupAudioContext(sound: HTMLAudioElement) {
            this.audioContext = new AudioContext();
            const source = this.audioContext.createMediaElementSource(sound);
            this.analyser = this.audioContext.createAnalyser();
            source.connect(this.analyser);
            this.analyser.connect(this.audioContext.destination);
            this.analyser.fftSize = 256;
            this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
        },
    },

    watch: {
        // Clamp widget position whenever panel opens/closes or tabs switch
        panelExpanded() {
            this.$nextTick(() => {
                this._clampToViewport();
                this._saveWidgetPosition();
            });
        },
        activePanel() {
            this.$nextTick(() => {
                this._clampToViewport();
                this._saveWidgetPosition();
            });
        },
    },

    mounted() {
        this.audio = CallsController.audio;
        this.video = CallsController.video;
        this.subtitlesActive = RoomController.subtitlesActive;

        // Register all controller events declaratively
        const events: EventEntry[] = [
            // CallsController
            [CallsController, "open", this.onOpenCall.bind(this)],
            [CallsController, "close", this.onCloseCall.bind(this)],
            [CallsController, "error-msg", this.onCallError.bind(this)],
            [CallsController, "joined", this.joined.bind(this)],
            [CallsController, "user-join", this.onUserJoin.bind(this)],
            [CallsController, "user-leave", this.onUserLeave.bind(this)],
            [CallsController, "user-change", this.onUserChange.bind(this)],
            [CallsController, "user-stream", this.onUserStream.bind(this)],
            [CallsController, "user-volume", this.onUserVolume.bind(this)],
            [CallsController, "self-change", this.onSelfChange.bind(this)],
            [CallsController, "ask-recording", this.onAskRecording.bind(this)],
            [CallsController, "ask-stop-recording", this.stopRecordUser.bind(this)],
            [CallsController, "ask-summary", this.onAskSummary.bind(this)],
            [CallsController, "warning-no-microphone", () => { this.micAvailable = false; }],
            [CallsController, "raised-hand", this.onRaisedHand.bind(this)],
            [CallsController, "translation-received", this.onTranslationReceived.bind(this)],
            // RoomController
            [RoomController, "room-roles", this.onRoomRoles.bind(this)],
            [RoomController, "stop-record", this.onStopRecordEvent.bind(this)],
            [RoomController, "stop-recording-user", this.stopRecordUser.bind(this)],
            [RoomController, "leave-while-recording", this.onLeaveWhileRecording.bind(this)],
            [RoomController, "subtitles-changed", this.onRoomSubtitlesChanged.bind(this)],
            // ChatController
            [ChatController, "chat-message", this.onGcwChatMessage.bind(this)],
            [ChatController, "chat-notice", this.onGcwChatNotice.bind(this)],
            [ChatController, "chat-error", this.onGcwChatError.bind(this)],
            [ChatController, "chat-delete-msg", this.onGcwChatDeleteMsg.bind(this)],
            [ChatController, "chat-delete-user", this.onGcwChatDeleteUser.bind(this)],
            [ChatController, "chat-reload", this.onGcwChatReload.bind(this)],
        ];
        (this as any)._cleanupEvents = useControllerEvents(events);

        // Sync roles from already-loaded room
        if (RoomController.room) {
            this.roomId = RoomController.room;
            this.canAdmin = RoomController.admin;
            this.canModerate = RoomController.mod;
            this.onGcwChatReload(RoomController.room);
        }

        // Draggable widget restore saved corner
        this._loadWidgetPosition();
        (this as any)._onResizeBound = this._clampToViewport.bind(this);
        window.addEventListener("resize", (this as any)._onResizeBound);
        (this as any)._onGlobalKeyDownBound = this.onGlobalKeyDown.bind(this);
        document.addEventListener("keydown", (this as any)._onGlobalKeyDownBound);

        this.$listenOnAppEvent("ask-leave-room", this.leaveCall.bind(this));

        this.refreshMediaDevices();
        if (navigator.mediaDevices?.addEventListener) {
            (this as any)._onMediaDevicesChanged = this.refreshMediaDevices.bind(this);
            navigator.mediaDevices.addEventListener("devicechange", (this as any)._onMediaDevicesChanged);
        }
    },

    beforeUnmount() {
        // Remove all controller event listeners
        if ((this as any)._cleanupEvents) {
            (this as any)._cleanupEvents();
        }

        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }

        // Destroy speaker tracker
        if (this.speakerTracker) {
            if ((this as any)._speakerChangedCb) {
                this.speakerTracker.off((this as any)._speakerChangedCb);
                (this as any)._speakerChangedCb = null;
            }
            this.speakerTracker.destroy();
            this.speakerTracker = null;
        }

        // Cancel any in-progress crossfade
        if ((this as any)._crossfadeTimeout) {
            clearTimeout((this as any)._crossfadeTimeout);
            (this as any)._crossfadeTimeout = null;
        }

        if (navigator.mediaDevices?.removeEventListener && (this as any)._onMediaDevicesChanged) {
            navigator.mediaDevices.removeEventListener("devicechange", (this as any)._onMediaDevicesChanged);
            (this as any)._onMediaDevicesChanged = null;
        }

        if ((this as any)._onGlobalKeyDownBound) {
            document.removeEventListener("keydown", (this as any)._onGlobalKeyDownBound);
            (this as any)._onGlobalKeyDownBound = null;
        }

        window.removeEventListener("resize", (this as any)._onResizeBound);
    },
});
</script>

<style scoped>
@import "@/style/components/global-call-widget.css";
</style>
